/**
 * CampaignsTab - Marketing campaigns management component (Refactored)
 * Main orchestrator that coordinates all campaign sub-components
 */
import { useState, useEffect } from 'react';
import { Plus, Trash2, Download, Calendar, BarChart3, FlaskConical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Import sub-components
import {
  CampaignSummary,
  CampaignCards,
  CampaignPerformanceChart,
  SignupsTable,
  QRCodesTab,
  SchedulerTab,
  AnalyticsTab,
  ABTestingTab,
  MarketingMaterials,
  CreateCampaignDialog,
  EditCampaignDialog,
  getThemeByKey
} from './campaigns';

export default function CampaignsTab({ authHeaders = {} }) {
  // Core data state
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [qrCodes, setQrCodes] = useState({});
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [promoStats, setPromoStats] = useState(null);
  
  // UI state
  const [activeView, setActiveView] = useState('overview');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [viewingAllSignups, setViewingAllSignups] = useState(false);
  const [signups, setSignups] = useState([]);
  const [signupsPagination, setSignupsPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: 100,
    has_next: false,
    has_prev: false
  });
  const [loadingSignups, setLoadingSignups] = useState(false);
  
  // Action state
  const [clearingStats, setClearingStats] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(null);
  const [sendingBlast, setSendingBlast] = useState(null);
  const [deletingCode, setDeletingCode] = useState(null);
  
  // Analytics state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [conversionMetrics, setConversionMetrics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  
  // A/B Testing state
  const [abVariants, setAbVariants] = useState({});
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [variantForm, setVariantForm] = useState({ title: '', description: '', variant: 'B' });
  
  // Campaign dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    title: '',
    description: '',
    footer: '',
    theme: 'teal',
    discount_percent: 10,
    is_enabled: true
  });

  // === EFFECTS ===
  
  // Fetch QR codes when campaigns are loaded
  useEffect(() => {
    if (allCampaigns.length > 0) {
      allCampaigns.forEach(campaign => {
        if (!qrCodes[campaign.campaign_id]) {
          fetchQrCodeForCampaign(campaign.campaign_id);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCampaigns]);
  
  useEffect(() => {
    if (authHeaders?.Authorization) {
      fetchReport();
      fetchSchedules();
      fetchIntegrationStatus();
      fetchPromoStats();
      fetchAllCampaigns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeaders]);
  
  useEffect(() => {
    if (activeView === 'analytics' && authHeaders?.Authorization && !analyticsData) {
      fetchAnalyticsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, authHeaders]);

  useEffect(() => {
    if (activeView === 'abtesting' && authHeaders?.Authorization && Object.keys(abVariants).length === 0) {
      fetchAllAbVariants();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, authHeaders]);

  // === DATA FETCHING ===
  
  const fetchReport = async () => {
    try {
      const res = await api.get('/admin/campaigns/report');
      setReport(res.data);
    } catch (err) {
      toast.error('Failed to load campaign report');
    }
    setLoading(false);
  };

  const fetchAllCampaigns = async () => {
    try {
      const res = await api.get('/admin/campaign-manager/campaigns');
      setAllCampaigns(res.data.campaigns || []);
    } catch (err) {
      console.error('Failed to fetch campaigns:', err);
    }
  };

  const fetchSchedules = async () => {
    try {
      const res = await api.get('/admin/campaigns/schedule');
      setSchedules(res.data);
    } catch (err) {
      console.error('Failed to load schedules');
    }
  };

  const fetchQrCodes = async () => {
    // Dynamically fetch QR codes for all campaigns (not hardcoded)
    // This will be called again when allCampaigns is populated
    if (allCampaigns.length > 0) {
      for (const campaign of allCampaigns) {
        try {
          const res = await api.get(`/campaigns/${campaign.campaign_id}/qr/base64?size=400`);
          setQrCodes(prev => ({ ...prev, [campaign.campaign_id]: res.data }));
        } catch (err) {
          console.error(`Failed to fetch QR code for campaign ${campaign.campaign_id}`);
        }
      }
    }
  };

  const fetchQrCodeForCampaign = async (campaignId) => {
    try {
      const res = await api.get(`/campaigns/${campaignId}/qr/base64?size=400`);
      setQrCodes(prev => ({ ...prev, [campaignId]: res.data }));
    } catch (err) {
      console.error(`Failed to fetch QR code for campaign ${campaignId}`);
    }
  };

  const fetchIntegrationStatus = async () => {
    try {
      const res = await api.get('/admin/integrations/status');
      setIntegrationStatus(res.data);
    } catch (err) {
      console.error('Failed to load integration status');
    }
  };

  const fetchPromoStats = async () => {
    try {
      const res = await api.get('/admin/promo-codes/stats');
      setPromoStats(res.data);
    } catch (err) {
      console.error('Failed to fetch promo stats');
    }
  };

  const fetchAnalyticsData = async () => {
    setLoadingAnalytics(true);
    try {
      const [analyticsRes, conversionRes] = await Promise.all([
        api.get('/admin/campaigns/analytics'),
        api.get('/admin/campaigns/conversion-metrics')
      ]);
      
      setAnalyticsData(analyticsRes.data);
      setConversionMetrics(conversionRes.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      toast.error('Failed to load analytics data');
    }
    setLoadingAnalytics(false);
  };

  const fetchAllAbVariants = async () => {
    setLoadingVariants(true);
    try {
      const variants = {};
      // Only fetch variants for campaigns that exist
      const campaignIds = allCampaigns.length > 0 
        ? allCampaigns.map(c => c.campaign_id) 
        : [];
      for (const id of campaignIds) {
        try {
          const res = await api.get(`/admin/campaigns/${id}/ab-variants`);
          variants[id] = res.data.variants || [];
        } catch (err) {
          // Skip individual failures
        }
      }
      setAbVariants(variants);
    } catch (err) {
      console.error('Failed to fetch A/B variants:', err);
      toast.error('Failed to load A/B variants');
    }
    setLoadingVariants(false);
  };

  // === SIGNUPS HANDLERS ===

  const viewSignups = async (campaignId, page = 1) => {
    setSelectedCampaign(campaignId);
    setViewingAllSignups(false);
    setLoadingSignups(true);
    try {
      const res = await api.get(`/admin/campaigns/signups?campaign_id=${campaignId}&page=${page}&limit=100`);
      setSignups(res.data.signups || []);
      setSignupsPagination(res.data.pagination || {
        current_page: 1,
        total_pages: 1,
        total_count: 0,
        per_page: 100,
        has_next: false,
        has_prev: false
      });
    } catch (err) {
      toast.error('Failed to load signups');
    }
    setLoadingSignups(false);
  };

  const viewAllSignups = async (page = 1) => {
    setSelectedCampaign(null);
    setViewingAllSignups(true);
    setLoadingSignups(true);
    try {
      const res = await api.get(`/admin/campaigns/signups?page=${page}&limit=100`);
      setSignups(res.data.signups || []);
      setSignupsPagination(res.data.pagination || {
        current_page: 1,
        total_pages: 1,
        total_count: 0,
        per_page: 100,
        has_next: false,
        has_prev: false
      });
    } catch (err) {
      toast.error('Failed to load signups');
    }
    setLoadingSignups(false);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > signupsPagination.total_pages) return;
    if (selectedCampaign) {
      viewSignups(selectedCampaign, newPage);
    } else if (viewingAllSignups) {
      viewAllSignups(newPage);
    }
  };

  const handleShowRecentOnly = () => {
    setSelectedCampaign(null);
    setViewingAllSignups(false);
    setSignups([]);
    setSignupsPagination({
      current_page: 1,
      total_pages: 1,
      total_count: 0,
      per_page: 100,
      has_next: false,
      has_prev: false
    });
  };

  // === ACTION HANDLERS ===

  const downloadQR = async (campaignId, size = 500) => {
    const link = document.createElement('a');
    link.href = `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/qr?size=${size}`;
    link.download = `bulaccino-campaign-${campaignId}-qr-${size}px.png`;
    link.click();
  };

  const clearAllStats = async () => {
    if (!window.confirm('Are you sure you want to clear ALL campaign signup statistics? This action cannot be undone.')) {
      return;
    }
    setClearingStats(true);
    try {
      const res = await api.delete('/admin/campaigns/clear-stats');
      toast.success(`Cleared ${res.data.deleted_count} signup records`);
      fetchReport();
      fetchPromoStats();
      setSignups([]);
    } catch (err) {
      toast.error(err.message || 'Failed to clear stats');
    }
    setClearingStats(false);
  };

  const deleteSignupCode = async (signupId) => {
    if (!window.confirm('Remove promo code from this signup?')) return;
    setDeletingCode(signupId);
    try {
      await api.delete(`/admin/signups/${signupId}/promo-code`);
      toast.success('Promo code removed');
      fetchReport();
      fetchPromoStats();
      if (selectedCampaign) viewSignups(selectedCampaign);
    } catch (err) {
      toast.error('Failed to remove code');
    }
    setDeletingCode(null);
  };

  const clearAllPromoCodes = async () => {
    if (!window.confirm('This will delete ALL promo codes. Are you sure?')) return;
    try {
      await api.delete('/admin/promo-codes/clear-all');
      toast.success('All promo codes cleared');
      fetchReport();
      fetchPromoStats();
    } catch (err) {
      toast.error('Failed to clear codes');
    }
  };

  // === SCHEDULER HANDLERS ===

  const updateSchedule = async (campaignId, updates) => {
    const currentSchedule = schedules.find(s => s.campaign_id === campaignId) || {};
    const newSchedule = { ...currentSchedule, ...updates };
    setSchedules(prev => prev.map(s => 
      s.campaign_id === campaignId ? newSchedule : s
    ));
  };

  const saveSchedule = async (campaignId) => {
    setSavingSchedule(campaignId);
    const schedule = schedules.find(s => s.campaign_id === campaignId);
    try {
      await api.put(`/admin/campaigns/schedule/${campaignId}`, schedule);
      toast.success(`Campaign ${campaignId} schedule saved!`);
    } catch (err) {
      toast.error('Failed to save schedule');
    }
    setSavingSchedule(null);
  };

  const sendBlast = async (campaignId, type) => {
    setSendingBlast({ campaignId, type });
    try {
      const res = await api.post(`/admin/campaigns/${campaignId}/send-blast?blast_type=${type}`);
      if (res.data.twilio_configured || res.data.mailchimp_configured) {
        toast.success(`Blast sent to ${res.data.total_signups} signups!`);
      } else {
        toast.info(`${res.data.total_signups} signups ready. Configure Twilio/Mailchimp to send.`);
      }
      fetchSchedules();
    } catch (err) {
      toast.error('Failed to send blast');
    }
    setSendingBlast(null);
  };

  // === A/B TESTING HANDLERS ===

  const saveAbVariant = async (campaignId) => {
    if (!variantForm.title || !variantForm.description) {
      toast.error('Title and description are required');
      return;
    }
    try {
      const params = new URLSearchParams({
        variant: variantForm.variant,
        title: variantForm.title,
        description: variantForm.description
      });
      await api.post(`/admin/campaigns/${campaignId}/ab-variants?${params}`);
      toast.success(`Variant ${variantForm.variant} saved`);
      setEditingVariant(null);
      setVariantForm({ title: '', description: '', variant: 'B' });
      fetchAllAbVariants();
    } catch (err) {
      toast.error('Failed to save variant');
    }
  };

  // === CAMPAIGN MANAGEMENT HANDLERS ===

  const resetCampaignForm = () => {
    setCampaignForm({
      title: '',
      description: '',
      footer: '',
      theme: 'teal',
      discount_percent: 10,
      is_enabled: true
    });
  };

  const handleCreateCampaign = async () => {
    if (!campaignForm.title.trim() || !campaignForm.description.trim()) {
      toast.error('Title and description are required');
      return;
    }
    setSavingCampaign(true);
    try {
      const res = await api.post('/admin/campaign-manager/campaigns', campaignForm);
      toast.success(`Campaign ${res.data.campaign.campaign_id} created!`);
      setShowCreateDialog(false);
      resetCampaignForm();
      fetchAllCampaigns();
      fetchReport();
      fetchQrCodeForCampaign(res.data.campaign.campaign_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create campaign');
    }
    setSavingCampaign(false);
  };

  const handleEditCampaign = async () => {
    if (!campaignForm.title.trim() || !campaignForm.description.trim()) {
      toast.error('Title and description are required');
      return;
    }
    setSavingCampaign(true);
    try {
      await api.put(`/admin/campaign-manager/campaigns/${editingCampaign.campaign_id}`, campaignForm);
      toast.success('Campaign updated!');
      setShowEditDialog(false);
      setEditingCampaign(null);
      fetchAllCampaigns();
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update campaign');
    }
    setSavingCampaign(false);
  };

  const openEditCampaignDialog = (campaign) => {
    setEditingCampaign(campaign);
    setCampaignForm({
      title: campaign.title,
      description: campaign.description,
      footer: campaign.footer || '',
      theme: campaign.theme || 'teal',
      discount_percent: campaign.discount_percent || 10,
      is_enabled: campaign.is_enabled !== false
    });
    setShowEditDialog(true);
  };

  const handleToggleCampaign = async (campaignId) => {
    try {
      const res = await api.patch(`/admin/campaign-manager/campaigns/${campaignId}/toggle`);
      toast.success(res.data.message);
      fetchAllCampaigns();
    } catch (err) {
      toast.error('Failed to toggle campaign');
    }
  };

  // === RENDER ===

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="campaigns-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl text-foreground">Marketing Campaigns</h1>
          <p className="text-muted-foreground mt-1">Track signups, manage QR codes, and schedule blasts</p>
        </div>
        <div className="flex items-center gap-4">
          <Button
            onClick={() => {
              resetCampaignForm();
              setShowCreateDialog(true);
            }}
            className="gap-2"
            data-testid="create-campaign-btn"
          >
            <Plus className="w-4 h-4" />
            Create Campaign
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/30"
            onClick={clearAllStats}
            disabled={clearingStats}
          >
            <Trash2 className="w-4 h-4 mr-1" />
            {clearingStats ? 'Clearing...' : 'Clear All Stats'}
          </Button>
          <div className="flex bg-muted rounded-full p-1">
            <button
              onClick={() => setActiveView('overview')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeView === 'overview' ? 'bg-card shadow text-foreground' : 'text-muted-foreground'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveView('qrcodes')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeView === 'qrcodes' ? 'bg-card shadow text-foreground' : 'text-muted-foreground'
              }`}
            >
              <Download className="w-4 h-4 inline mr-1" />
              QR Codes
            </button>
            <button
              onClick={() => setActiveView('scheduler')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeView === 'scheduler' ? 'bg-card shadow text-foreground' : 'text-muted-foreground'
              }`}
            >
              <Calendar className="w-4 h-4 inline mr-1" />
              Scheduler
            </button>
            <button
              onClick={() => setActiveView('analytics')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeView === 'analytics' ? 'bg-card shadow text-foreground' : 'text-muted-foreground'
              }`}
            >
              <BarChart3 className="w-4 h-4 inline mr-1" />
              Analytics
            </button>
            <button
              onClick={() => setActiveView('abtesting')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeView === 'abtesting' ? 'bg-card shadow text-foreground' : 'text-muted-foreground'
              }`}
            >
              <FlaskConical className="w-4 h-4 inline mr-1" />
              A/B Testing
            </button>
          </div>
        </div>
      </div>

      {/* Total Signups Summary */}
      <CampaignSummary
        report={report}
        allCampaigns={allCampaigns}
        onViewAllSignups={viewAllSignups}
      />

      {/* Overview Tab */}
      {activeView === 'overview' && (
        <>
          <CampaignCards
            report={report}
            allCampaigns={allCampaigns}
            schedules={schedules}
            onViewSignups={viewSignups}
            onEditCampaign={openEditCampaignDialog}
            onToggleCampaign={handleToggleCampaign}
            onDownloadQR={downloadQR}
          />
          
          <CampaignPerformanceChart report={report} />
          
          <SignupsTable
            report={report}
            signups={signups}
            selectedCampaign={selectedCampaign}
            viewingAllSignups={viewingAllSignups}
            loadingSignups={loadingSignups}
            signupsPagination={signupsPagination}
            promoStats={promoStats}
            deletingCode={deletingCode}
            onViewAllSignups={viewAllSignups}
            onShowRecentOnly={handleShowRecentOnly}
            onPageChange={handlePageChange}
            onDeleteSignupCode={deleteSignupCode}
            onClearAllPromoCodes={clearAllPromoCodes}
          />
        </>
      )}

      {/* QR Codes Tab */}
      {activeView === 'qrcodes' && (
        <QRCodesTab
          report={report}
          allCampaigns={allCampaigns}
          qrCodes={qrCodes}
          onDownloadQR={downloadQR}
        />
      )}

      {/* Scheduler Tab */}
      {activeView === 'scheduler' && (
        <SchedulerTab
          report={report}
          allCampaigns={allCampaigns}
          schedules={schedules}
          integrationStatus={integrationStatus}
          savingSchedule={savingSchedule}
          sendingBlast={sendingBlast}
          onUpdateSchedule={updateSchedule}
          onSaveSchedule={saveSchedule}
          onSendBlast={sendBlast}
        />
      )}

      {/* Analytics Tab */}
      {activeView === 'analytics' && (
        <AnalyticsTab
          report={report}
          analyticsData={analyticsData}
          conversionMetrics={conversionMetrics}
          loadingAnalytics={loadingAnalytics}
          onRefreshAnalytics={fetchAnalyticsData}
        />
      )}

      {/* A/B Testing Tab */}
      {activeView === 'abtesting' && (
        <ABTestingTab
          report={report}
          allCampaigns={allCampaigns}
          abVariants={abVariants}
          loadingVariants={loadingVariants}
          editingVariant={editingVariant}
          variantForm={variantForm}
          onSetEditingVariant={setEditingVariant}
          onSetVariantForm={setVariantForm}
          onSaveVariant={saveAbVariant}
          onRefreshVariants={fetchAllAbVariants}
        />
      )}

      {/* Marketing Materials */}
      <MarketingMaterials report={report} allCampaigns={allCampaigns} />

      {/* Dialogs */}
      <CreateCampaignDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        campaignForm={campaignForm}
        onFormChange={setCampaignForm}
        onSubmit={handleCreateCampaign}
        saving={savingCampaign}
      />

      <EditCampaignDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        editingCampaign={editingCampaign}
        campaignForm={campaignForm}
        onFormChange={setCampaignForm}
        onSubmit={handleEditCampaign}
        saving={savingCampaign}
      />
    </div>
  );
}
